import { useEffect, useState } from "react";
import { fetchAdminDefinition, createDraft, saveDraft, publishDraft, discardDraft } from "@/villa142/api";
import { ConfirmModal } from "@/villa142/admin/ConfirmModal";

const uid = () => Math.random().toString(36).slice(2, 10);
const INPUT_MODELS = ["long_text", "short_text", "choice", "multi_choice", "scale", "people_list", "items_list", "voice_only"];

const parseLines = (s) => String(s || "").split("\n").map((x) => x.trim()).filter(Boolean);
const parseFields = (s) => parseLines(s).map((line) => {
  const [key, label] = line.split("|").map((x) => x.trim());
  return { key: key || label?.toLowerCase().replace(/\s+/g, "_") || uid(), label: label || key };
});
const fieldsToText = (fields) => (fields || []).map((f) => `${f.key} | ${f.label}`).join("\n");

const QuestionEditor = ({ q, onChange, onClose }) => {
  const cfg = q.config || {};
  const set = (patch) => onChange({ ...q, ...patch });
  const setCfg = (patch) => onChange({ ...q, config: { ...cfg, ...patch } });
  const details = cfg.details || null;
  return (
    <div className="v142-qeditor" data-testid={`v142-qeditor-${q.id}`}>
      <div className="v142-qe-grid">
        <label className="v142-mini-label" htmlFor={`qe-label-${q.id}`}>Question text</label>
        <textarea id={`qe-label-${q.id}`} className="v142-followup-input" rows={2} value={q.label || ""} onChange={(e) => set({ label: e.target.value })} data-testid={`v142-qe-label-${q.id}`} />
        <label className="v142-mini-label" htmlFor={`qe-helper-${q.id}`}>Helper copy (optional)</label>
        <textarea id={`qe-helper-${q.id}`} className="v142-followup-input" rows={2} value={q.helper || ""} onChange={(e) => set({ helper: e.target.value || null })} />
        <label className="v142-mini-label" htmlFor={`qe-input-${q.id}`}>Input model</label>
        <select id={`qe-input-${q.id}`} className="v142-select" value={q.input} onChange={(e) => set(e.target.value === "voice_only" ? { input: "voice_only", voice: true, uploads: null, config: { voiceMax: cfg.voiceMax || 3, voiceSeconds: cfg.voiceSeconds || 180 } } : { input: e.target.value })} data-testid={`v142-qe-input-${q.id}`}>
          {INPUT_MODELS.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        {q.input === "voice_only" && (
          <p className="v142-support" data-testid={`v142-qe-voiceonly-note-${q.id}`}>Voice-only question — clients record voice notes only, with no written, image, or structured fields. Set the maximum note count and duration below.</p>
        )}

        {(q.input === "choice" || q.input === "multi_choice") && (
          <>
            <label className="v142-mini-label">Choices (one per line)</label>
            <textarea className="v142-followup-input" rows={4} value={(cfg.options || []).join("\n")} onChange={(e) => setCfg({ options: parseLines(e.target.value) })} data-testid={`v142-qe-options-${q.id}`} />
          </>
        )}
        {q.input === "scale" && (
          <div className="v142-qe-row">
            <input className="v142-input v142-input-small" placeholder="Left label" value={cfg.left || ""} onChange={(e) => setCfg({ left: e.target.value })} />
            <input className="v142-input v142-input-small" placeholder="Right label" value={cfg.right || ""} onChange={(e) => setCfg({ right: e.target.value })} />
          </div>
        )}
        {(q.input === "people_list") && (
          <>
            <label className="v142-mini-label">Card fields (key | Label, one per line)</label>
            <textarea className="v142-followup-input" rows={3} value={fieldsToText(cfg.fields)} onChange={(e) => setCfg({ fields: parseFields(e.target.value) })} />
          </>
        )}
        {(q.input === "people_list" || q.input === "items_list") && (
          <div className="v142-qe-row">
            <input className="v142-input v142-input-small" placeholder="Item label (e.g. Piece)" value={cfg.itemLabel || ""} onChange={(e) => setCfg({ itemLabel: e.target.value })} />
            {q.input === "items_list" && (
              <label className="v142-qe-check"><input type="checkbox" checked={Boolean(cfg.itemUploads)} onChange={(e) => setCfg({ itemUploads: e.target.checked })} /> Images per item</label>
            )}
          </div>
        )}
        {q.input === "choice" && (
          <details className="v142-qe-details">
            <summary className="v142-mini-label">Conditional follow-up inside this question</summary>
            <div className="v142-qe-row">
              <input className="v142-input v142-input-small" placeholder="Show when (comma separated choices)" value={(details?.when || []).join(", ")} onChange={(e) => setCfg({ details: { ...(details || { input: "long_text" }), when: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) } })} />
              <select className="v142-select" value={details?.input || ""} onChange={(e) => setCfg({ details: e.target.value ? { ...(details || {}), input: e.target.value } : undefined })}>
                <option value="">No follow-up</option>
                <option value="long_text">Long text</option>
                <option value="items_list">Repeatable list</option>
                <option value="fields">Structured fields</option>
                <option value="cards">Repeatable cards</option>
              </select>
            </div>
            {details?.input === "items_list" && (
              <input className="v142-input v142-input-small" placeholder="Item label (e.g. Role)" value={details.itemLabel || ""} onChange={(e) => setCfg({ details: { ...details, itemLabel: e.target.value } })} />
            )}
            {details?.input === "fields" && (
              <textarea className="v142-followup-input" rows={3} placeholder="key | Label (one per line)" value={fieldsToText(details.fields)} onChange={(e) => setCfg({ details: { ...details, fields: parseFields(e.target.value) } })} />
            )}
            {details?.input === "cards" && (
              <>
                <input className="v142-input v142-input-small" placeholder="Intro line above the cards" value={details.intro || ""} onChange={(e) => setCfg({ details: { ...details, intro: e.target.value } })} />
                <input className="v142-input v142-input-small" placeholder="Card label (e.g. Consideration)" value={details.itemLabel || ""} onChange={(e) => setCfg({ details: { ...details, itemLabel: e.target.value } })} />
                <textarea className="v142-followup-input" rows={4} placeholder="key | Label (one per line)" value={fieldsToText(details.fields)} onChange={(e) => setCfg({ details: { ...details, fields: parseFields(e.target.value) } })} />
              </>
            )}
          </details>
        )}
        {(q.input === "long_text" || q.input === "short_text") && (
          <details className="v142-qe-details">
            <summary className="v142-mini-label">Optional chips alongside the writing field</summary>
            <input className="v142-input v142-input-small" placeholder="Chips heading (e.g. Pastimes that fit)" value={cfg.chipsLabel || ""} onChange={(e) => setCfg({ chipsLabel: e.target.value || undefined })} />
            <textarea className="v142-followup-input" rows={4} placeholder="One chip per line" value={(cfg.chips || []).join("\n")} onChange={(e) => { const c = parseLines(e.target.value); setCfg({ chips: c.length ? c : undefined }); }} data-testid={`v142-qe-chips-${q.id}`} />
            <label className="v142-qe-check"><input type="checkbox" checked={Boolean(cfg.chipsOther)} onChange={(e) => setCfg({ chipsOther: e.target.checked || undefined })} /> Free-text field when “Other” is selected</label>
          </details>
        )}

        <div className="v142-qe-row v142-qe-toggles">
          <label className="v142-qe-check"><input type="checkbox" checked={Boolean(q.voice)} onChange={(e) => set({ voice: e.target.checked })} data-testid={`v142-qe-voice-${q.id}`} /> Voice notes</label>
          {q.voice && (
            <>
              <label className="v142-qe-check">Up to
                <select className="v142-select" value={cfg.voiceMax || 3} data-testid={`v142-qe-voicemax-${q.id}`} onChange={(e) => setCfg({ voiceMax: Number(e.target.value) })}>
                  {[1, 2, 3].map((n) => <option key={n} value={n}>{n}</option>)}
                </select> notes
              </label>
              <label className="v142-qe-check">Max
                <select className="v142-select" value={cfg.voiceSeconds || 180} data-testid={`v142-qe-voicesec-${q.id}`} onChange={(e) => setCfg({ voiceSeconds: Number(e.target.value) })}>
                  <option value={60}>1 min</option>
                  <option value={120}>2 min</option>
                  <option value={180}>3 min</option>
                </select>
              </label>
            </>
          )}
          <label className="v142-qe-check"><input type="checkbox" checked={Boolean(q.uploads)} onChange={(e) => set({ uploads: e.target.checked ? { max: 3, captionPrompt: "A few words, if you like" } : null })} /> Image uploads</label>
          {q.uploads && (
            <input className="v142-input v142-input-small v142-qe-max" type="number" min={1} max={6} aria-label="Maximum uploads" value={q.uploads.max} onChange={(e) => set({ uploads: { ...q.uploads, max: Number(e.target.value) || 1 } })} />
          )}
          <label className="v142-qe-check"><input type="checkbox" checked={Boolean(cfg.note)} onChange={(e) => setCfg({ note: e.target.checked || undefined })} /> Optional note</label>
          {(q.input === "choice" || q.input === "multi_choice") && (
            <label className="v142-qe-check"><input type="checkbox" checked={Boolean(cfg.reflection)} onChange={(e) => setCfg({ reflection: e.target.checked || undefined })} data-testid={`v142-qe-reflection-${q.id}`} /> Overall reflection field</label>
          )}
          <label className="v142-qe-check"><input type="checkbox" checked={Boolean(cfg.tags)} onChange={(e) => setCfg({ tags: e.target.checked || undefined })} /> Colour/material tags</label>
        </div>

        <details className="v142-qe-details">
          <summary className="v142-mini-label">Conditional display (show only when…)</summary>
          <div className="v142-qe-row">
            <input className="v142-input v142-input-small" placeholder="Question ID (e.g. social_entertain)" value={q.conditional?.question || ""} onChange={(e) => set({ conditional: e.target.value ? { ...(q.conditional || { show: [], default_show: true }), question: e.target.value } : null })} data-testid={`v142-qe-cond-${q.id}`} />
            <input className="v142-input v142-input-small" placeholder="Show when (comma separated)" value={(q.conditional?.show || []).join(", ")} onChange={(e) => set({ conditional: { ...(q.conditional || { question: "", default_show: true }), show: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) } })} />
          </div>
        </details>
        <p className="v142-qe-id">Code: {q.code || "—"} · ID: {q.id}</p>
      </div>
      <button type="button" className="v142-btn v142-btn-small" onClick={onClose} data-testid={`v142-qe-done-${q.id}`}>Done</button>
    </div>
  );
};

export default function EditorView() {
  const [state, setState] = useState(null); // {published, draft, versions}
  const [chapters, setChapters] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saved, setSaved] = useState("idle");
  const [preview, setPreview] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const data = await fetchAdminDefinition();
    setState(data);
    setChapters(data.draft ? data.draft.chapters : null);
  };
  useEffect(() => { load(); }, []);

  if (!state) return <p className="v142-copy">Loading…</p>;

  const startDraft = async () => {
    await createDraft();
    await load();
  };

  const persistDraft = async (next) => {
    setChapters(next);
    setSaved("saving");
    try {
      await saveDraft(next);
      setSaved("saved");
    } catch {
      setSaved("error");
    }
  };

  const updateQ = (ci, qi, q) => {
    const next = chapters.map((ch, i) => (i !== ci ? ch : { ...ch, questions: ch.questions.map((x, j) => (j === qi ? q : x)) }));
    persistDraft(next);
  };

  const moveQ = (ci, qi, dir) => {
    const qs = [...chapters[ci].questions];
    const t = qi + dir;
    if (t < 0 || t >= qs.length) return;
    [qs[qi], qs[t]] = [qs[t], qs[qi]];
    persistDraft(chapters.map((ch, i) => (i === ci ? { ...ch, questions: qs } : ch)));
  };

  const moveChapter = (ci, dir) => {
    const chs = [...chapters];
    const t = ci + dir;
    if (t < 0 || t >= chs.length) return;
    [chs[ci], chs[t]] = [chs[t], chs[ci]];
    persistDraft(chs);
  };

  const addQ = (ci) => {
    const q = { id: `${chapters[ci].id}_q_${uid()}`, label: "New question", helper: null, input: "long_text", config: {}, voice: false, uploads: null, active: true, conditional: null };
    persistDraft(chapters.map((ch, i) => (i === ci ? { ...ch, questions: [...ch.questions, q] } : ch)));
    setEditing(q.id);
  };

  const duplicateQ = (ci, qi) => {
    const src = chapters[ci].questions[qi];
    const copy = { ...JSON.parse(JSON.stringify(src)), id: `${src.id}_copy_${uid()}` };
    const qs = [...chapters[ci].questions];
    qs.splice(qi + 1, 0, copy);
    persistDraft(chapters.map((ch, i) => (i === ci ? { ...ch, questions: qs } : ch)));
  };

  const doPublish = async () => {
    setBusy(true);
    try {
      await publishDraft();
      setConfirm(null);
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="v142-editor" data-testid="v142-editor">
      <div className="v142-admin-controls">
        <div>
          <p className="v142-support">
            Published: version {state.published?.version}{state.draft ? ` · Draft in progress: version ${state.draft.version}` : ""}
            {saved === "saving" && " · Saving draft…"}
            {saved === "saved" && " · Draft saved"}
            {saved === "error" && " · Draft could not save — retry an edit"}
          </p>
        </div>
        <div className="v142-admin-actions">
          {!state.draft && (
            <button className="v142-btn v142-btn-small" data-testid="v142-start-draft-btn" onClick={startDraft}>
              Start editing (draft v{(state.published?.version || 1) + 1})
            </button>
          )}
          {state.draft && (
            <>
              <button className="v142-btn v142-btn-small" data-testid="v142-preview-toggle" onClick={() => setPreview(!preview)}>
                {preview ? "Back to editing" : "Preview draft"}
              </button>
              <button className="v142-btn v142-btn-small" data-testid="v142-discard-draft-btn" onClick={async () => { await discardDraft(); await load(); }}>
                Discard draft
              </button>
              <button className="v142-btn v142-btn-small v142-btn-solid" data-testid="v142-publish-btn" onClick={() => setConfirm(true)}>
                Publish version {state.draft.version}
              </button>
            </>
          )}
        </div>
      </div>

      {!state.draft && (
        <p className="v142-copy">The published questionnaire is live for the clients. Start a draft to make changes — nothing is visible to clients until you publish.</p>
      )}

      {state.draft && chapters && preview && (
        <div className="v142-preview" data-testid="v142-draft-preview">
          {chapters.filter((c) => c.active !== false).map((ch) => (
            <div className="v142-review-chapter" key={ch.id}>
              <h2 className="v142-review-chtitle">{ch.title}</h2>
              <p className="v142-support">{ch.intro}</p>
              {ch.questions.filter((q) => q.active !== false).map((q) => (
                <div className="v142-review-row" key={q.id}>
                  <div className="v142-review-body">
                    <p className="v142-review-q">{q.label}</p>
                    {q.helper && <p className="v142-support">{q.helper}</p>}
                    <p className="v142-entry-meta">
                      {q.input}{q.voice ? " · voice" : ""}{q.uploads ? ` · uploads (${q.uploads.max})` : ""}{q.conditional ? ` · shown when ${q.conditional.question} ∈ [${q.conditional.show.join(", ")}]` : ""}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {state.draft && chapters && !preview && chapters.map((ch, ci) => (
        <div className="v142-editor-chapter" key={ch.id} data-testid={`v142-editor-chapter-${ch.id}`}>
          <div className="v142-editor-chhead">
            <div className="v142-editor-chmeta">
              <input className="v142-input v142-input-small v142-editor-chtitle" aria-label="Chapter title" value={ch.title} onChange={(e) => persistDraft(chapters.map((c, i) => (i === ci ? { ...c, title: e.target.value } : c)))} />
              <textarea className="v142-followup-input" rows={2} aria-label="Chapter introduction" value={ch.intro || ""} onChange={(e) => persistDraft(chapters.map((c, i) => (i === ci ? { ...c, intro: e.target.value } : c)))} />
            </div>
            <div className="v142-editor-chctrl">
              <button type="button" className="v142-link" aria-label="Move chapter up" onClick={() => moveChapter(ci, -1)}>↑</button>
              <button type="button" className="v142-link" aria-label="Move chapter down" onClick={() => moveChapter(ci, 1)}>↓</button>
            </div>
          </div>
          {ch.id === "visual-cues" && (
            <p className="v142-support">Visual studies are managed in the Visual studies tab and appear at the start of this chapter.</p>
          )}
          {ch.questions.map((q, qi) => (
            <div className={`v142-editor-qrow${q.active === false ? " is-inactive" : ""}`} key={q.id} data-testid={`v142-editor-q-${q.id}`}>
              <div className="v142-editor-qmain">
                <span className="v142-editor-qlabel">{q.label}</span>
                <span className="v142-entry-meta">{q.code ? `${q.code} · ` : ""}{q.input}{q.active === false ? " · disabled" : ""}</span>
              </div>
              <div className="v142-editor-qctrl">
                <button type="button" className="v142-link" aria-label="Move up" onClick={() => moveQ(ci, qi, -1)}>↑</button>
                <button type="button" className="v142-link" aria-label="Move down" onClick={() => moveQ(ci, qi, 1)}>↓</button>
                <button type="button" className="v142-link" data-testid={`v142-editor-edit-${q.id}`} onClick={() => setEditing(editing === q.id ? null : q.id)}>Edit</button>
                <button type="button" className="v142-link" onClick={() => duplicateQ(ci, qi)}>Duplicate</button>
                <button type="button" className="v142-link" data-testid={`v142-editor-toggle-${q.id}`} onClick={() => updateQ(ci, qi, { ...q, active: q.active === false })}>
                  {q.active === false ? "Restore" : "Disable"}
                </button>
              </div>
              {editing === q.id && <QuestionEditor q={q} onChange={(nq) => updateQ(ci, qi, nq)} onClose={() => setEditing(null)} />}
            </div>
          ))}
          <button type="button" className="v142-add-row" data-testid={`v142-add-question-${ch.id}`} onClick={() => addQ(ci)}>+ Add question</button>
        </div>
      ))}

      <ConfirmModal
        open={Boolean(confirm)}
        title={`Publish version ${state.draft?.version}`}
        body="Publishing makes this draft the live questionnaire for the clients. Past responses keep the version they were answered under."
        confirmLabel="Publish"
        busy={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={doPublish}
      />
    </div>
  );
}
